import React, {createRef, useState, useContext, useEffect} from 'react';
import { DragDropContext, Droppable, Draggable } from '@react-forked/dnd';
import {useAuth0} from "@auth0/auth0-react";
import __ from "lodash";
import MaterialTable from "@material-table/core";
import {
  defaultComponents,
  defaultComponentsScroll,
  MaterialTableBody,
  MaterialTableBodyRow
} from '../../../controls/Material-Table-Components';
import {Checkbox, Tooltip, useTheme} from "@material-ui/core";

import SystemContext from "../../../SystemContext";

import AirportLookup from "../../Airports/components/AirportsLookup";
import {ColumnWidth, ValidatorColumn} from "../../../blocks/ColumnHelper";
import EnquiryCargoValidator from "../validators/EnquiryCargoValidator";
import {MaskedNumber, MaskedText } from '../../../controls';
import { useQueryClient } from 'react-query';

const formatChars={
  "U": "[A-Za-z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "n": "-",
  "x": "."
}

const SectionHeading = (props) => {
  const { children, ...other } = props;
  return (
    <div style={{marginTop: 10, marginBottom: 2, textAlign: "left", fontSize: "14px", fontWeight: "bold", ...other}}>
      {children}
    </div>
  );
}

const EnquiryCargoEditor = (props) => {
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const tableRef = createRef({state: {lastEditingRow: false}});
  const {isAuthenticated} = useAuth0();
  const eventBus = SystemData.eventBus;
  const record = props.record;
  const oldProps = __.cloneDeep(props);

  useEffect(()=> {
    const addCargo = async () => {
      if (tableRef  && tableRef.current) {
        tableRef.current.dataManager.changeRowEditing();
        tableRef.current.setState({
          ...tableRef.current.dataManager.getRenderState(),
          showAddRow: !tableRef.current.state.showAddRow,
        });
      }
  }
    if (eventBus) {
      eventBus.bind('EnquiryCargoEditor.addCargo', addCargo);
      return () => {
        eventBus.unbind("EnquiryCargoEditor.addCargo", addCargo);
      }
    }

  }, [eventBus, tableRef]);

  const {classes} = props;

  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const cargo = record.cargo||[];

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { padding: "4px 2px" },
      headerStyle: { padding: "2px 2px" },
      editable: state.canEdit?"always":"never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    const validatorColumn = ValidatorColumn(EnquiryCargoValidator, false);

    return [
      {...validatorColumn, resizable: false},
      {
        ...baseColumn,
        ...ColumnWidth(60,[5,2],undefined,undefined,baseColumn.headerStyle,undefined,false),
        title: "Origin",
        field: "originAirportCode",
        initialEditValue: "",
        editComponent: (props) => {
          return (
            <AirportLookup
              value={props.value}
              returnType="object"
              onChange={(returnObject)=>{
                if (returnObject.found) {
                  const originAirportCode = returnObject.data.airportCode;
                  props.onChange(originAirportCode);
                } else {
                  props.onChange(returnObject.value);
                }
              }}
            />
          )
        },
      },
      {
        ...baseColumn,
        ...ColumnWidth(60,2,undefined,undefined,baseColumn.headerStyle,undefined,false),
        title: "Dest",
        field: "destinationAirportCode",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            returnType="object"
            onChange={(returnObject)=>{
              if (returnObject.found) {
                const data = {
                  ...props.rowData,
                  destinationAirportCode: returnObject.data.airportCode,
                  alternateAirportCode: returnObject.data.alternateAirportCode,
                  alternateAirportIcaoCode: returnObject.data.alternateAirportIcaoCode
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(95,2,undefined,undefined,undefined,undefined,false),
        title: "Loose (kg)",
        field: "payloadLoose",
        type: "numeric",
        initialEditValue: 0,
        render: (rowData) => (
          <MaskedNumber
            displayType="text"
            defaultValue={rowData.payloadLoose||0}
            value={rowData.payloadLoose||0}
            decimalScale={0}
            fixedDecimalScale={true}
            valueCap={props.weightUom==="TON"?150:150000}
            suffix={props.weightUom==="TON"?" tons":" kg"}
            inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        editComponent: (props) => (
          <MaskedNumber
            displayType="input"
            defaultValue={props.value||0}
            value={props.value}
            decimalScale={0}
            fixedDecimalScale={true}
            valueCap={props.weightUom==="TON"?150:150000}
            suffix={props.weightUom==="TON"?" tons":" kg"}
            inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(95,2,undefined,undefined,undefined,undefined,false),
        title: "Unitised (kg)",
        field: "payloadUnitised",
        type: "numeric",
        initialEditValue: 0,
        render: (rowData) => (
          <MaskedNumber
            displayType="text"
            defaultValue={rowData.payloadUnitised}
            value={rowData.payloadUnitised}
            decimalScale={0}
            fixedDecimalScale={true}
            valueCap={props.weightUom==="TON"?150:150000}
            suffix={props.weightUom==="TON"?" tons":" kg"}
            inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        editComponent: (props) => (
          <MaskedNumber
            displayType="input"
            defaultValue={props.value||0}
            value={props.value||0}
            decimalScale={0}
            fixedDecimalScale={true}
            valueCap={props.weightUom==="TON"?150:150000}
            suffix={props.weightUom==="TON"?" tons":" kg"}
            inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(95,2,undefined,undefined,undefined,undefined,false),
        title: "BUP Count",
        field: "bupCount",
        type: "numeric",
        initialEditValue: 0,
        render: (rowData) => (
          <MaskedNumber
            displayType="text"
            defaultValue={rowData.bupCount}
            value={rowData.bupCount}
            decimalScale={0}
            fixedDecimalScale={true}
            valueCap={50}
            inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        editComponent: (props) => (
          <MaskedNumber
            displayType="input"
            defaultValue={props.value||0}
            value={props.value||0}
            decimalScale={0}
            fixedDecimalScale={true}
            valueCap={50}
            inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(1,2,undefined,{width: 6, minWidth: 6, maxWidth: 6},baseColumn.headerStyle,undefined,"NONE"),
        title: " ",
        field: "",
        render: ()=>(<></>),
        editable: "never",
      },
      {
        ...baseColumn,
        ...ColumnWidth(80,undefined,undefined,undefined,false),
        title: "Handling",
        field: "includeHandling",
        emptyValue: true,
        initialEditValue: true,
        type: "boolean",
        render: rowData =>
          <Checkbox
            checked={(rowData.includeHandling === 1 || rowData.includeHandling === true) ?? true}
            style={{padding: 0}}
          />,
        editComponent: props =>
          <Checkbox
            checked={(props.rowData.includeHandling === 1 || props.rowData.includeHandling === true) ?? true}
            style={{padding: 0}}
            onChange={ (event, checked) => {
              props.onChange(checked)
            }}
          />,
      },
      {
        ...baseColumn,
        ...ColumnWidth(80,undefined,undefined,undefined,false),
        title: "Secure",
        field: "cargoSecure",
        emptyValue: false,
        initialEditValue: false,
        type: "boolean",
        render: rowData =>
          <Checkbox
            checked={(rowData.cargoSecure === 1 || rowData.cargoSecure === true) ?? true}
            style={{padding: 0}}
          />,
        editComponent: props =>
          <Checkbox
            checked={(props.rowData.cargoSecure === 1 || props.rowData.cargoSecure === true) ?? true}
            style={{padding: 0}}
            onChange={ (event, checked) => {
              props.onChange(checked)
            }}
          />,
      },
      {
        ...baseColumn,
        ...ColumnWidth(400),
        title: "Notes",
        field: "notes",
        initialEditValue: "",
        render: rowData => (
          <Tooltip title={rowData.notes} >
            <div style={{textOverflow: 'ellipsis', width: 330, overflow: 'hidden'}}>{rowData.notes}</div>
          </Tooltip>
        ),
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(200)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
    ];
  };

  const onDragEnd = (dragProps) => {
    if (!dragProps) return;
    if (!dragProps.source || !dragProps.destination) return;
    if (dragProps.source.index === dragProps.source.destination) return;

    if (Array.isArray(cargo)) {
      let records = [...cargo];
      let sourceRecord = records.splice(dragProps.source.index,1);
      const part1 = [...records].slice(0,dragProps.destination.index);
      const part2 = [...records].slice(dragProps.destination.index);
      const newRecords = [...part1, ...sourceRecord, ...part2];
      cargo.splice(0, cargo.length, ...newRecords);
      props.onChange({record: {...props.record, legs: cargo}, oldRecord: props.record});
    }
  }

  const bodyRef = createRef();

  return (isAuthenticated &&
        <div className={classes.enquiryCargoEditor}>
          <SectionHeading>Cargo Details</SectionHeading>
          <MaterialTable
            tableRef={tableRef}
            data={cargo}
            style={{
              backgroundColor: "#fdfdfd",
              boxShadow: "none",
              border: "2px solid #aaaaaa",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              search: false,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.childTableHeader.backgroundColor,
                color: theme.palette.childTableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              detailPanelType: "single",
              showTitle: false,
              toolbar: false,
              padding: 'dense',
              paging: false,
              filtering: false,
              draggable: false,
            }}
            components={{
              ...defaultComponents,
              Body: (props) => (
                <DragDropContext onDragEnd={onDragEnd}>
                <Droppable
                  droppableId={"singleColumnDroppableAreaThusStaticInput"}
                >
                  {(provided) => (
                    <MaterialTableBody
                      {...props}
                      ref={bodyRef}
                      forwardedRef={provided.innerRef}
                      placeHolder={provided.placeholder}
                      rowHeight={40}
                      overscanRowCount={10}
                      addVirtualScrolling={false}
//                      bodyHeight={300}
                      extraEditHeight={46}
                      headerHeight={46}
                    />
                  )}
                </Droppable>
              </DragDropContext>
              ),
              Row: (props) => (
                <Draggable
                  draggableId={props.data.tableData.id.toString()}
                  index={props.data.tableData.id}
                >
                  {(provided) => {
                    return (
                      <MaterialTableBodyRow
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        {...props}
                        forwardedRef={provided.innerRef}
                      />
                    );
                  }}
                </Draggable>
              ),
            }}
            title="Cargo Consignments"
            columns={getColumns({...props, canEdit: state.canEdit})}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                ? (newData) => new Promise((resolve, reject) => {
                  if (Array.isArray(cargo)) {
                    cargo.push(newData);
                    props.onChange({record: {...props.record, cargo}, oldRecord: oldProps.record, resolve, reject});
                  } else {
                    reject();
                  }
                })
                : undefined,
              onRowUpdate: (newData, oldData) =>
                new Promise((resolve, reject) => {
                  const cargoIdx = __.findIndex(cargo, (consignment) => {
                    const tripData = __.omit(consignment, ["tableData"]);
                    const oldRecord = __.omit(oldData, ["tableData"]);
                    return __.isEqual(tripData, oldRecord);
                  }); //sectors.indexOf(oldData);
                  if (cargoIdx >= 0) {
                    cargo[cargoIdx] = newData;
                    props.onChange({record: {...props.record, cargo}, oldRecord: oldProps.record, resolve, reject});
                  } else {
                    reject();
                  }
                }),
              onRowDelete: (oldData) =>
                new Promise((resolve, reject) => {
                  const cargoIdx = __.findIndex(cargo, (consignment) => {
                    const tripData = __.omit(consignment, ["tableData"]);
                    const oldRecord = __.omit(oldData, ["tableData"]);
                    return __.isEqual(tripData, oldRecord);
                  }); //sectors.indexOf(oldData);
                  if (cargoIdx >= 0) {
                    cargo.splice(cargoIdx, 1);
                    props.onChange({record: {...props.record, cargo}, oldRecord: oldProps.record, resolve, reject});
                  } else {
                    reject();
                  }
                }),
            }}
        />
      </div>
  );
}

export default EnquiryCargoEditor;


